import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { BookOpen, Plus, X, Tag, Download, Lock, FileText, Trophy } from "lucide-react";

const MOOD_TAGS = [
  { id: "happy", label: "Happy", color: "#E5B95F" },
  { id: "calm", label: "Calm", color: "#4A6B53" },
  { id: "sad", label: "Sad", color: "#8A918C" },
  { id: "anxious", label: "Anxious", color: "#C86B55" },
  { id: "angry", label: "Angry", color: "#9A4D3B" },
  { id: "hopeful", label: "Hopeful", color: "#B5C7A3" },
  { id: "grateful", label: "Grateful", color: "#D2A679" },
];

const GUIDED_PROMPTS = [
  "What made you smile today, even just a little?",
  "Write about something you're proud of this week.",
  "Describe a moment where you felt safe recently.",
  "What would you tell your younger self right now?",
  "What is one thing you need to let go of?",
  "Write a letter to someone. You don't have to send it.",
  "What does healing look like for you today?",
  "Name three things that brought you comfort this week.",
];

export default function JournalPage() {
  const [activeSection, setActiveSection] = useState("journal");
  const [entries, setEntries] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [moodTag, setMoodTag] = useState("");
  const [entryType, setEntryType] = useState("free");
  const [filterMood, setFilterMood] = useState("");
  // Incident log fields
  const [incidentDate, setIncidentDate] = useState("");
  const [incidentTime, setIncidentTime] = useState("");
  const [incidentLocation, setIncidentLocation] = useState("");
  const [incidentWhat, setIncidentWhat] = useState("");
  const [incidentWho, setIncidentWho] = useState("");
  // Wins
  const [wins, setWins] = useState([]);
  const [newWin, setNewWin] = useState("");

  const fetchEntries = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterMood) params.append("mood_tag", filterMood);
      const res = await api.get(`/journals?${params}`);
      setEntries(res.data);
    } catch (e) { console.error(e); }
  }, [filterMood]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Load wins from localStorage + API
  const loadWins = useCallback(async () => {
    const local = JSON.parse(localStorage.getItem("sovha_my_wins") || "[]");
    try {
      const res = await api.get(`/wins`);
      // Merge API wins with local, dedupe by text
      const apiWins = res.data;
      const allTexts = new Set(apiWins.map(w => w.text));
      const merged = [...apiWins, ...local.filter(w => !allTexts.has(w.text))];
      setWins(merged);
    } catch {
      setWins(local);
    }
  }, []);

  useEffect(() => { loadWins(); }, [loadWins]);

  const addWin = async () => {
    const text = newWin.trim();
    if (!text || text.length > 200) return;
    const win = { id: Date.now().toString(), text, timestamp: new Date().toISOString() };
    // Save to localStorage
    const local = JSON.parse(localStorage.getItem("sovha_my_wins") || "[]");
    local.unshift(win);
    localStorage.setItem("sovha_my_wins", JSON.stringify(local));
    // Also try API
    try { await api.post(`/wins`, { text }); } catch {}
    setNewWin("");
    loadWins();
  };

  const deleteWin = (id) => {
    const local = JSON.parse(localStorage.getItem("sovha_my_wins") || "[]");
    localStorage.setItem("sovha_my_wins", JSON.stringify(local.filter(w => w.id !== id)));
    try { api.delete(`/wins/${id}`); } catch {}
    setWins(prev => prev.filter(w => w.id !== id));
  };

  const generatePrompt = () => {
    const p = GUIDED_PROMPTS[Math.floor(Math.random() * GUIDED_PROMPTS.length)];
    setContent(p + "\n\n");
    setEntryType("guided");
    setShowForm(true);
  };

  const openIncidentLog = () => {
    setShowForm(true); setEntryType("incident"); setContent(""); setTitle("");
    setIncidentDate(new Date().toISOString().split("T")[0]);
    setIncidentTime(new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }));
    setIncidentLocation(""); setIncidentWhat(""); setIncidentWho("");
  };

  const saveEntry = async () => {
    let finalContent = content;
    let finalTitle = title;
    if (entryType === "incident") {
      finalContent = `Date: ${incidentDate}\nTime: ${incidentTime}\nLocation: ${incidentLocation}\n\nWhat happened:\n${incidentWhat}\n\nAnyone present:\n${incidentWho}`;
      finalTitle = finalTitle || `Incident Log - ${incidentDate}`;
    }
    if (!finalContent.trim()) return;
    try {
      await api.post(`/journal`, { title: finalTitle.trim(), content: finalContent.trim(), mood_tag: moodTag, entry_type: entryType });
      resetForm(); fetchEntries();
    } catch (e) { console.error(e); }
  };

  const resetForm = () => {
    setTitle(""); setContent(""); setMoodTag(""); setEntryType("free");
    setShowForm(false);
    setIncidentDate(""); setIncidentTime(""); setIncidentLocation(""); setIncidentWhat(""); setIncidentWho("");
  };

  const deleteEntry = async (id) => {
    try { await api.delete(`/journal/${id}`); fetchEntries(); } catch (e) { console.error(e); }
  };

  const exportAndEncrypt = () => {
    if (entries.length === 0) return;
    const exportData = entries.map(e => ({ title: e.title, content: e.content, mood: e.mood_tag, type: e.entry_type, date: e.timestamp }));
    const jsonStr = JSON.stringify(exportData, null, 2);
    const encoded = btoa(unescape(encodeURIComponent(jsonStr)));
    const blob = new Blob([encoded], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `sovha-journal-${new Date().toISOString().split("T")[0]}.enc`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-8 py-12 sm:py-20" data-testid="journal-page">
      <div className="text-center mb-12">
        <p className="text-sage text-sm uppercase tracking-[0.15em] font-bold mb-3">My Journal</p>
        <h1 className="font-heading text-4xl sm:text-5xl text-earth-800 mb-4 tracking-tight">Your private space</h1>
        <p className="text-earth-600 text-base max-w-lg mx-auto">Write freely. No one sees this but you.</p>
      </div>

      {/* Section Tabs: Journal / My Wins */}
      <div className="flex justify-center gap-3 mb-8" data-testid="journal-section-tabs">
        <button onClick={() => setActiveSection("journal")} data-testid="journal-tab-entries"
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-150 ${
            activeSection === "journal"
              ? "text-white shadow-sm"
              : "bg-transparent text-[#4A7C5F]"
          }`}
          style={activeSection === "journal"
            ? { backgroundColor: "#4A7C5F" }
            : { border: "1.5px solid #4A7C5F" }
          }>
          Journal
        </button>
        <button onClick={() => setActiveSection("wins")} data-testid="journal-tab-wins"
          className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-150 flex items-center gap-1.5 ${
            activeSection === "wins"
              ? "text-white shadow-sm"
              : "bg-transparent text-[#C17D3C]"
          }`}
          style={activeSection === "wins"
            ? { backgroundColor: "#C17D3C" }
            : { border: "1.5px solid #C17D3C" }
          }>
          <Trophy size={14} /> My Wins
        </button>
      </div>

      {/* ===== MY WINS SECTION ===== */}
      {activeSection === "wins" && (
        <div data-testid="wins-board">
          <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-6">
            <h3 className="font-heading text-xl text-earth-800 mb-4 flex items-center gap-2">
              <Trophy size={20} className="text-warmyellow" /> Add a win
            </h3>
            <p className="text-earth-500 text-sm mb-4">Big or small, every win counts. What went well today?</p>
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <input value={newWin} onChange={(e) => setNewWin(e.target.value.slice(0, 200))} onKeyDown={(e) => e.key === "Enter" && addWin()}
                  placeholder="I did something brave today..." data-testid="win-input"
                  className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full focus:ring-2 focus:ring-warmyellow/30 focus:outline-none placeholder:text-earth-400 text-sm" />
                <span className="absolute right-3 top-3.5 text-earth-300 text-[10px]">{newWin.length}/200</span>
              </div>
              <button onClick={addWin} data-testid="add-win-btn" disabled={!newWin.trim()}
                className="bg-warmyellow text-earth-800 rounded-2xl px-6 py-3 font-medium text-sm hover:-translate-y-0.5 hover:shadow-md transition-all duration-300 disabled:opacity-40">Add</button>
            </div>
          </div>

          {wins.length === 0 ? (
            <div className="text-center py-12"><Trophy size={40} className="mx-auto text-earth-300 mb-4" /><p className="text-earth-500">Your wins board is empty. Start celebrating yourself.</p></div>
          ) : (
            <div className="space-y-3">
              {wins.map((w) => (
                <div key={w.id} className="bg-warmyellow/5 rounded-2xl p-5 flex items-start justify-between gap-4 group border border-warmyellow/10">
                  <div className="flex items-start gap-3">
                    <Trophy size={16} className="text-warmyellow mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-earth-800 font-medium">{w.text}</p>
                      <p className="text-earth-400 text-xs mt-1">{new Date(w.timestamp).toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}</p>
                    </div>
                  </div>
                  <button onClick={() => deleteWin(w.id)} className="opacity-0 group-hover:opacity-100 text-earth-400 hover:text-earth-600 transition-all"><X size={14} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ===== JOURNAL SECTION ===== */}
      {activeSection === "journal" && (
        <>
          {/* Actions */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-4">
            <button onClick={() => { setShowForm(true); setEntryType("free"); }} data-testid="new-entry-btn"
              className="rounded-full px-6 py-3 font-medium flex items-center gap-2 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 text-white"
              style={{ backgroundColor: "#4A7C5F" }}>
              <Plus size={18} /> New Entry
            </button>
            <button onClick={generatePrompt} data-testid="guided-prompt-btn"
              className="rounded-full px-6 py-3 font-medium transition-all duration-300 hover:bg-[#4A7C5F]/10"
              style={{ border: "1.5px solid #4A7C5F", color: "#4A7C5F" }}>
              Guided Prompt
            </button>
            <button onClick={openIncidentLog} data-testid="incident-log-btn"
              className="rounded-full px-6 py-3 font-medium transition-all duration-300 flex items-center gap-2 hover:bg-[#8B6A5A]/10"
              style={{ border: "1.5px solid #8B6A5A", color: "#8B6A5A" }}>
              <FileText size={16} /> Incident Log
            </button>
          </div>

          {/* Export & Encrypt */}
          <div className="flex justify-center mb-8">
            <button onClick={exportAndEncrypt} data-testid="export-encrypt-btn" disabled={entries.length === 0}
              className="flex items-center gap-2 px-6 py-3 rounded-full font-medium text-sm transition-all duration-300 disabled:opacity-40 disabled:hover:translate-y-0 hover:bg-[#6B7280]/10"
              style={{ border: "1.5px solid #6B7280", color: "#6B7280" }}>
              <Download size={16} /> <Lock size={14} /> Export & Encrypt
            </button>
          </div>

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2 mb-8" data-testid="journal-filters">
            <button onClick={() => setFilterMood("")}
              className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!filterMood ? "text-white" : "text-[#4A7C5F]"}`}
              style={!filterMood ? { backgroundColor: "#4A7C5F" } : { backgroundColor: "#4A7C5F18", color: "#4A7C5F" }}>All moods</button>
            {MOOD_TAGS.map((m) => (
              <button key={m.id} onClick={() => setFilterMood(filterMood === m.id ? "" : m.id)}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                style={filterMood === m.id
                  ? { backgroundColor: m.color, color: "white" }
                  : { backgroundColor: m.color + "15", color: m.color }
                }>{m.label}</button>
            ))}
          </div>

          {/* Entry Form */}
          {showForm && (
            <div className="bg-white rounded-[2rem] p-8 shadow-[0_8px_30px_rgba(45,49,46,0.04)] border border-earth-200/50 mb-8 animate-fade-in-up" data-testid="journal-form">
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-heading text-xl text-earth-800">
                  {entryType === "incident" ? "Incident Log" : entryType === "guided" ? "Guided Entry" : "Free Write"}
                </h3>
                <button onClick={resetForm} className="text-earth-400 hover:text-earth-600"><X size={20} /></button>
              </div>

              {entryType === "incident" ? (
                <>
                  <div className="bg-terracotta-light/50 rounded-2xl p-4 mb-6">
                    <p className="text-terracotta text-sm font-medium">This log can be used for personal records. Document what happened with as much detail as you feel comfortable sharing.</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Date</label>
                      <input type="date" value={incidentDate} onChange={(e) => setIncidentDate(e.target.value)} data-testid="incident-date"
                        className="bg-earth-100 border-0 rounded-2xl px-4 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none text-sm" />
                    </div>
                    <div>
                      <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Time</label>
                      <input type="text" value={incidentTime} onChange={(e) => setIncidentTime(e.target.value)} placeholder="e.g., 3:30 PM" data-testid="incident-time"
                        className="bg-earth-100 border-0 rounded-2xl px-4 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none text-sm placeholder:text-earth-400" />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Location</label>
                    <input type="text" value={incidentLocation} onChange={(e) => setIncidentLocation(e.target.value)} placeholder="Where did it happen?" data-testid="incident-location"
                      className="bg-earth-100 border-0 rounded-2xl px-4 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none text-sm placeholder:text-earth-400" />
                  </div>
                  <div className="mb-4">
                    <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">What happened</label>
                    <textarea value={incidentWhat} onChange={(e) => setIncidentWhat(e.target.value)} placeholder="Describe what happened..." rows={5} data-testid="incident-what"
                      className="bg-earth-100 border-0 rounded-2xl px-4 py-4 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none resize-none leading-relaxed text-sm placeholder:text-earth-400" />
                  </div>
                  <div className="mb-4">
                    <label className="text-earth-600 text-xs font-semibold uppercase tracking-wider mb-1 block">Anyone present</label>
                    <input type="text" value={incidentWho} onChange={(e) => setIncidentWho(e.target.value)} placeholder="Who else was there? (optional)" data-testid="incident-who"
                      className="bg-earth-100 border-0 rounded-2xl px-4 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none text-sm placeholder:text-earth-400" />
                  </div>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="journal-title-input"
                    className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full mb-4 focus:ring-2 focus:ring-sage/30 focus:outline-none placeholder:text-earth-400 font-heading text-lg" />
                </>
              ) : (
                <>
                  <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (optional)" data-testid="journal-title-input"
                    className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full mb-4 focus:ring-2 focus:ring-sage/30 focus:outline-none placeholder:text-earth-400 font-heading text-lg" />
                  <textarea value={content} onChange={(e) => setContent(e.target.value)} placeholder="Start writing..." rows={8} data-testid="journal-content-input"
                    className="bg-earth-100 border-0 rounded-2xl px-5 py-4 text-earth-800 w-full mb-4 focus:ring-2 focus:ring-sage/30 focus:outline-none placeholder:text-earth-400 resize-none leading-relaxed" />
                </>
              )}

              <div className="mb-6">
                <p className="text-earth-600 text-sm font-medium mb-2 flex items-center gap-1"><Tag size={14} /> Tag a mood</p>
                <div className="flex flex-wrap gap-2">
                  {MOOD_TAGS.map((m) => (
                    <button key={m.id} onClick={() => setMoodTag(moodTag === m.id ? "" : m.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${moodTag === m.id ? "border-current" : "border-transparent"}`}
                      style={{ backgroundColor: moodTag === m.id ? m.color + "20" : m.color + "10", color: m.color }}>{m.label}</button>
                  ))}
                </div>
              </div>

              <button onClick={saveEntry} data-testid="save-journal-btn"
                className="bg-sage text-earth-50 rounded-full px-8 py-3 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300">Save Entry</button>
            </div>
          )}

          {/* Entries List */}
          {entries.length === 0 && !showForm ? (
            <div className="text-center py-16" data-testid="journal-empty">
              <BookOpen size={40} className="mx-auto text-earth-300 mb-4" />
              <p className="text-earth-500">Your journal is empty. Your first entry is waiting.</p>
            </div>
          ) : (
            <div className="space-y-4" data-testid="journal-entries">
              {entries.map((entry) => {
                const mood = MOOD_TAGS.find((m) => m.id === entry.mood_tag);
                return (
                  <div key={entry.id} className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(45,49,46,0.04)] border border-earth-200/50 group">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          {entry.title && <h4 className="font-heading text-lg text-earth-800">{entry.title}</h4>}
                          {mood && <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider" style={{ backgroundColor: mood.color + "15", color: mood.color }}>{mood.label}</span>}
                          {entry.entry_type !== "free" && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-earth-200/50 text-earth-500">{entry.entry_type === "chat_summary" ? "Chat Summary" : entry.entry_type}</span>
                          )}
                        </div>
                        <p className="text-earth-600 text-sm leading-relaxed whitespace-pre-wrap line-clamp-4">{entry.content}</p>
                        <p className="text-earth-400 text-xs mt-3">{new Date(entry.timestamp).toLocaleDateString("en-US", { weekday: "short", year: "numeric", month: "short", day: "numeric" })}</p>
                      </div>
                      <button onClick={() => deleteEntry(entry.id)} className="opacity-0 group-hover:opacity-100 text-earth-400 hover:text-earth-600 transition-all flex-shrink-0"><X size={16} /></button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}
